import { format, isMatch, isValid, parse } from 'date-fns';

import { SALES_IMPORT_ALIGNMENT } from '../constants';

import templateUrl from './salesImportTableComponent.html';

class SalesImportTableCtrl {
  constructor(PersonaCollection, $scope) {
    'ngInject';

    Object.assign(this, { PersonaCollection, $scope, SALES_IMPORT_ALIGNMENT });

    this.rowFilter = this.rowFilter.bind(this); // hardbind for filterFilter
  }

  $onChanges({ rows, previewMode }) {
    if (rows && rows?.currentValue?.[0]) {
      this.selectedColumns = {};
      this.rows[0].cells.forEach((cell, index) => {
        this.selectedColumns[index] = !(index === 0 && this.firstIsHeader);
      });
    }

    if (previewMode) {
      this.$scope.$broadcast('vsRepeatTrigger');
    }
  }

  _getTrueColSize() {
    const MODAL_WIDTH = 1024;
    const COL_MIN_WIDTH = 150;
    const STICKY_COL_WIDTH = this.align === SALES_IMPORT_ALIGNMENT.HORIZONTAL ? 150 : 58;

    let dividor;
    if (this.align === SALES_IMPORT_ALIGNMENT.HORIZONTAL) {
      const filter = this.colFilter(this.rows[0]);
      dividor = this.rows[0].cells.filter(filter).length;
    } else {
      dividor = this.rows.filter(this.rowFilter.bind(this)).length;
    }

    const flexWidth = (MODAL_WIDTH - STICKY_COL_WIDTH) / dividor;
    return Math.max(flexWidth, COL_MIN_WIDTH);
  }

  getRowSize() {
    if (this.align === SALES_IMPORT_ALIGNMENT.HORIZONTAL) {
      return 57;
    }

    return this._getTrueColSize();
  }

  getColSize() {
    if (this.align === SALES_IMPORT_ALIGNMENT.HORIZONTAL) {
      return this._getTrueColSize();
    }

    return 41;
  }

  rowFilter(row) {
    return !this.previewMode || row.type;
  }

  colFilter(row) {
    return (cell) => {
      const index = row.cells.indexOf(cell);
      return !this.previewMode || (this.isValidColumn(index) && this.selectedColumns[index]);
    };
  }

  get selectedAll() {
    if (!this.selectedColumns) {
      return false;
    }

    let values = Object.values(this.selectedColumns);
    if (this.firstIsHeader) {
      values = values.slice(1);
    }
    return values.every(Boolean);
  }

  set selectedAll(val) {
    if (this.selectedColumns) {
      Object.keys(this.selectedColumns).forEach((key) => {
        this.selectedColumns[key] = val;
      });
    }
  }

  getRowTypeLabel(row) {
    if (!row.type) {
      return '';
    }

    let label;
    const f = (option) => {
      if (option.submenu) {
        option.submenu.forEach(f);
      } else if (option.value === row.type) {
        label = option.toggleLabel || option.label;
      }
    };

    row.options.forEach(f);
    return label;
  }

  initRow(row) {
    if (row.options.length === 1) {
      row.type = row.options[0].value;
    }
  }

  initTransposedCell(row, col) {
    return this.rows[row].cells[col];
  }

  get error() {
    if (this.duplicateLocationsSelected()) {
      return 'The same location cannot be set more than once';
    }

    if (this.noLocationsSelected()) {
      return 'At least one location has to be selected';
    }

    if (this.duplicateDateSelected()) {
      return 'Cannot select date more than once';
    }

    if (this.noDateSelected()) {
      return `Date ${
        this.align === SALES_IMPORT_ALIGNMENT.VERTICAL ? 'column' : 'row'
      } must be selected`;
    }

    return undefined;
  }

  set error(v) {
    // eslint-disable-line
  }

  columnCheckedClick(index, event) {
    if (event.target.tagName === 'INPUT') {
      if (
        (event.metaKey || event.ctrlKey) &&
        this.selectedColumns[index] === this.lastColumnChecked?.[1]
      ) {
        for (
          let i = Math.min(index, this.lastColumnChecked[0]);
          i <= Math.max(index, this.lastColumnChecked[0]);
          i++
        ) {
          this.selectedColumns[i] = this.selectedColumns[index];
        }
      }

      this.lastColumnChecked = [index, this.selectedColumns[index]];
    }
  }

  duplicateLocationsSelected() {
    const locationIds = new Set();
    for (const row of this.rows) {
      if (row.type && row.type.locationId) {
        if (locationIds.has(row.type.locationId)) {
          return true;
        }

        locationIds.add(row.type.locationId);
      }
    }
  }

  duplicateDateSelected() {
    let dateAlreadyFound = false;
    for (const row of this.rows) {
      if (row.type && row.type.date) {
        if (dateAlreadyFound) {
          return true;
        }

        dateAlreadyFound = true;
      }
    }

    return false;
  }

  noLocationsSelected() {
    for (const row of this.rows) {
      if (row.type && row.type.locationId) {
        return false;
      }
    }

    return true;
  }

  noDateSelected() {
    for (const row of this.rows) {
      if (row.type && row.type.date) {
        return false;
      }
    }

    return true;
  }

  isValidColumn(colIndex) {
    return this.rows
      .filter((r) => r.type)
      .every(({ cells, type }) => {
        if (type.date) {
          const date = cells[colIndex].value;
          const dateFormat = type.date;

          // @INFO it requires extra checking against two digit year
          // more info: https://github.com/date-fns/date-fns/issues/942
          const parsedData = parse(date, dateFormat, new Date());
          if (!isValid(parsedData)) {
            return false;
          }

          return isMatch(date, dateFormat) && format(parsedData, dateFormat) === date;
        }

        return cells[colIndex].type === 'number';
      });
  }

  atLeastOneColumnValid() {
    return this.rows[0].cells.some((cell, index) => this.isValidColumn(index));
  }

  get noValidSales() {
    return this.previewMode && !this.atLeastOneColumnValid();
  }

  set noValidSales(val) {
    //
  }
}

export default {
  templateUrl,
  controllerAs: 'ctrl',
  controller: SalesImportTableCtrl,
  bindings: {
    rows: '<',
    align: '<',
    error: '=',
    firstIsHeader: '<',
    selectedColumns: '=?',
    previewMode: '<?',
    noValidSales: '=?',
  },
};
